import { PendingButton } from './PendingButton';
import type { ReactNode } from 'react';

// THE MESSAGE PAGE — a centred column that says one thing and offers what to do
// about it. Brand at the top, a title, one paragraph, the actions, and a quiet
// line beneath. Built once here, from named parts, so every page of this shape
// in the app is the same page with different words (Freddi, 2026-09-14:
// "modularise each part of that now and globalise that as a generic component").
//
// It grew up on the customer side — the door a registration link opens, the
// sign-in page, the dead-link and locked-out pages — but there is nothing
// customer-specific left in it: the desk's own signed-out, denied and
// sign-in-error pages are this shape too, and can adopt it.
//
// THE PARTS ARE EXPORTED AS WELL AS THE WHOLE. A page that needs the brand block
// on its own (Home's header), or a button in a card (Home's empty state), takes
// the part rather than re-drawing it — which is the only way the mark stays one
// size and the buttons one height across the app.

/** The paw in a rounded square, in the brand colour — 28 beside a name, 56 opening a page. */
export function SquareMark({ size = 28 }: { size?: 28 | 56 }) {
  const glyph = size === 56 ? 30 : 16;
  return (
    <span className={'mp-mark' + (size === 56 ? ' lg' : '')} aria-hidden="true">
      <svg
        width={glyph}
        height={glyph}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="4" r="2" />
        <circle cx="18" cy="8" r="2" />
        <circle cx="20" cy="16" r="2" />
        <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" />
      </svg>
    </span>
  );
}

/**
 * Mark, name and eyebrow. `stacked` is the page-opening form — mark above the
 * name, both larger; the default is the header form, mark beside the name.
 */
export function MessageBrand({
  name = 'Beeches Brook',
  eyebrow = 'Boarding Kennels',
  stacked = false,
}: {
  name?: string;
  eyebrow?: string | null;
  stacked?: boolean;
}) {
  return (
    <>
      <div className={'mp-brand' + (stacked ? ' stacked' : '')}>
        <SquareMark size={stacked ? 56 : 28} />
        <span className="mp-brand-name">{name}</span>
      </div>
      {stacked && eyebrow && <p className="mp-eyebrow">{eyebrow}</p>}
    </>
  );
}

export type ActionTone =
  /** The one thing the page is urging. */
  | 'primary'
  /** A real choice that is not the urged one — an outline. */
  | 'quiet'
  /** WhatsApp's own green: the control that leaves the app. */
  | 'wa';

export interface ActionButtonProps {
  tone?: ActionTone;
  icon?: ReactNode;
  children: ReactNode;
  /**
   * A link. Given `external`, it opens in a new tab with the referrer WITHHELD —
   * `noreferrer`, not only `noopener`. The customer's door lives at a URL that
   * IS the registration token, and the Pages Referrer-Policy already trims a
   * cross-origin referrer to the origin; this is the belt to that brace, on the
   * element, so a page served without the header still leaks nothing to
   * WhatsApp (security review, 2026-09-14).
   */
  href?: string;
  external?: boolean;
  /** Sync, or a promise the button stays busy on until it settles. */
  onClick?: () => unknown;
  disabled?: boolean;
  title?: string;
  /** The owner's own busy. */
  busy?: boolean;
  /** The tap takes the customer to another page; stay busy until it does. */
  leaves?: boolean;
}

/**
 * One button, one height (46px — the floor for a thumb), full width of its
 * stack. Renders as a real link when given `href`, so it works before any
 * script runs; a button otherwise. `disabled` is only honoured on the button
 * form — a disabled link is a link that lies about being one.
 */
export function ActionButton({ tone = 'quiet', icon, children, href, external, onClick, disabled, title, busy, leaves }: ActionButtonProps) {
  // The one busy rule (PendingButton): a same-tab link, an action that leaves
  // the page, or a promise — each shows the spinner and refuses a second tap.
  return (
    <PendingButton className={'mp-btn ' + tone} icon={icon} href={href} external={external} onClick={onClick} disabled={disabled} title={title} busy={busy} leaves={leaves}>
      {children}
    </PendingButton>
  );
}

/**
 * The actions, stacked full width, the urged one first.
 *
 * ONE RULE AT EVERY WIDTH. The column is 360px wide at any viewport, so it is
 * phone-shaped everywhere: a thumb needs no horizontal aiming and 44pt of
 * height, and that is the shape every dead-end page a phone user has ever seen
 * already takes. Weight comes from tone — quiet beside primary — never from a
 * second layout.
 */
export function ActionStack({ children }: { children: ReactNode }) {
  return <div className="mp-actions">{children}</div>;
}

export interface MessagePageProps {
  title: string;
  /** The one paragraph under the title. */
  lead: ReactNode;
  /** Whatever this page asks the reader to do — usually an ActionStack. */
  actions?: ReactNode;
  /** The quiet line beneath — a caveat, or where to go if none of it worked. */
  foot?: ReactNode;
  /** Override the brand block; the default is the kennel's own. */
  brand?: ReactNode;
}

export function MessagePage({ title, lead, actions, foot, brand }: MessagePageProps) {
  return (
    <main className="mp-page">
      <div className="mp-card">
        {brand ?? <MessageBrand stacked />}
        <h1 className="mp-title">{title}</h1>
        <p className="mp-lead">{lead}</p>
        {actions}
        {foot && <p className="mp-foot">{foot}</p>}
      </div>
    </main>
  );
}
